import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_BASE_INTERVAL = 1000;
const RECONNECT_MAX_INTERVAL = 30000;
const RECONNECT_JITTER_RATIO = 0.25;
const RECONNECT_SYNC_MIN_DELAY = 1000;
const RECONNECT_SYNC_MAX_DELAY = 5000;

const withJitter = (delay, maximum = RECONNECT_MAX_INTERVAL) =>
  Math.min(
    maximum,
    Math.round(delay * (1 + RECONNECT_JITTER_RATIO * (Math.random() * 2 - 1)))
  );

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.shouldReconnect = true;
    this.consumer = createConsumer(websocketURL);
    this.configureConnectionMonitor();
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          if (!this.shouldReconnect) return;

          BaseActionCableConnector.isDisconnected = true;
          clearTimeout(this.reconnectSyncTimer);
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.reconnectSyncTimer = null;
    this.reconnectCheckAttempt = 0;
    this.presenceTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      this.presenceTimer = setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.scheduleReconnectSync();
      BaseActionCableConnector.isDisconnected = false;
      this.reconnectCheckAttempt = 0;
    } else {
      this.consumer.connect();
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    const baseDelay = Math.min(
      RECONNECT_BASE_INTERVAL * 2 ** this.reconnectCheckAttempt,
      RECONNECT_MAX_INTERVAL
    );
    this.reconnectCheckAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, withJitter(baseDelay));
  };

  configureConnectionMonitor = () => {
    const monitor = this.consumer.connection?.monitor;
    if (!monitor) return;

    monitor.getPollInterval = () => {
      const baseDelay = Math.min(
        RECONNECT_BASE_INTERVAL * 2 ** monitor.reconnectAttempts,
        RECONNECT_MAX_INTERVAL
      );
      return withJitter(baseDelay);
    };
  };

  scheduleReconnectSync = () => {
    clearTimeout(this.reconnectSyncTimer);
    const delay = Math.round(
      RECONNECT_SYNC_MIN_DELAY +
        (RECONNECT_SYNC_MAX_DELAY - RECONNECT_SYNC_MIN_DELAY) * Math.random()
    );
    this.reconnectSyncTimer = setTimeout(() => this.onReconnect(), delay);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    clearTimeout(this.reconnectSyncTimer);
    clearTimeout(this.presenceTimer);
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
